/**
 * LensThread — the ONE merged communications lens (P3, 2026-07-09).
 *
 * ONE thread, nothing lost. It reads WhatsApp from List A (the live
 * `wa_conversations` store, via `lensDataSource(filter)` → the secure
 * `waListConversations`/`waGetThreadPage` callables) and internal notes + email
 * from List B (`projects/{vp}/messages`, via the notes/email reader), MERGES
 * them by timestamp, and renders the REAL Team-Inbox <Thread/> + its <Composer/>
 * — never a redraw.
 *
 * Used by all three surfaces (contact record → Communications tab; the
 * /comms/:contact|:project routes; the project page + mobile sheet) so they are
 * literally the same lens, sliced two ways:
 *   · mode:'contact' → filter { contactId }
 *   · mode:'project' → filter { projectId }
 *
 * NO DUPLICATES: List B is filtered to `channel !== 'wa'` AND `type !== 'crossRef'`
 * (done in the reader), so the only home of WhatsApp is List A — a message that
 * exists in both stores (any WhatsApp message does) is shown exactly once.
 *
 * SEND PATHS PRESERVED: WhatsApp goes through the real <Composer/> (24h window,
 * ⚡ 📋 📎 😀). Internal notes (postMessage) and email (sendEmail) are wired via
 * the Thread's opt-in `extraChannels` switcher. Nothing the surfaces can do
 * today is lost.
 *
 * EMPTY-WHATSAPP: a contact/project with only notes/email still renders — a
 * synthetic empty-WA conversation carries the note/email timeline; the WhatsApp
 * tab honestly says it isn't available yet (muted line, no dead control) while
 * the note/email composers work.
 *
 * SCROLL: the reused `.ti-canvas` is the SOLE scroller (single-scroll rule); the
 * host is `flex:1; min-height:0` and its parent bounds the height.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Thread, { type LensExtraChannels } from '../screens/Thread';
import {
  lensDataSource,
  LIVE_POLL_MS,
  type InboxDataSource,
  type OlderMessagesResult,
  type ThreadData,
} from '../lib/inboxStore';
import { fallbackRegistry, subscribeNumberRegistry } from '../lib/numbers';
import { fetchInboxMeta } from '../lib/adminApi';
import { visibleCanned, cannedVars } from '../lib/sendPicker';
import { seedToCanned, CANNED_SEED } from '../lib/cannedSeed';
import type {
  CannedReply,
  InboxConversation,
  NumberRegistry,
  ThreadEvent,
} from '../lib/types';
import type { AttachmentInput, ConversationFilter } from '../../../lib/messagesStore';
import type { Message } from '../../../lib/messageModel';
import { useAuth } from '../../../lib/auth';
import {
  subscribeContactNotesEmail,
  subscribeProjectNotesEmail,
} from '../../../lib/contactThread';
import { postMessage } from '../../../lib/conversation';
import { sendEmail } from '../../../lib/outboundSend';
import { listBToInboxMessage } from './listBMap';
import './LensThread.css';

export type LensThreadProps =
  | {
      mode: 'contact';
      contactId: string;
      /** Contact display name — shown in empty copy. */
      contactName?: string;
      /** Project used for note/email sends (a contact may have a primary project). */
      primaryProjectId?: string;
    }
  | {
      mode: 'project';
      projectId: string;
      /** The linked client — the email/note contact target. */
      clientId?: string;
      /** Client display name — shown in empty copy. */
      clientName?: string;
    };

/* Map a WA send error to the same honest copy the Team Inbox shows. */
function sendErrorText(e: unknown): string {
  const msg = (e as { message?: string })?.message || '';
  if (/send_blocked:window_closed_media_blocked/.test(msg)) {
    return (
      'The 24-hour window closed while sending — a file cannot be sent ' +
      'outside it. Try again once the client messages.'
    );
  }
  if (/send_blocked/.test(msg)) {
    return 'WhatsApp blocked that send (the window closed). The message did not go out.';
  }
  return 'Send failed — the message did not go out. Try again.';
}
function templateErrorText(e: unknown): string {
  const msg = (e as { message?: string })?.message || '';
  if (/template_param_mismatch/.test(msg)) {
    return 'That template needs all its blanks filled before it can go out. Fill them and try again.';
  }
  if (/send_blocked:not_approved|template_not_approved/.test(msg)) {
    return 'That template is not approved for this number yet. Pick another or ask an admin.';
  }
  return 'The template did not go out. Try again.';
}

/** One WhatsApp conversation-picker chip (only when a lens has >1 WA thread). */
function ConvChip({
  label,
  sub,
  pressed,
  unread,
  onClick,
}: {
  label: string;
  sub?: string | null;
  pressed: boolean;
  unread: number;
  onClick: () => void;
}) {
  return (
    <button type="button" className="lt-conv-chip" aria-pressed={pressed} onClick={onClick}>
      <span className="lt-conv-chip-label">{label}</span>
      {sub && <span className="lt-conv-chip-sub">{sub}</span>}
      {unread > 0 && (
        <span className="lt-conv-chip-unread" aria-label={`${unread} unread`}>
          {unread}
        </span>
      )}
    </button>
  );
}

export default function LensThread(props: LensThreadProps) {
  const { user, role } = useAuth();
  const isAdmin = role === 'admin';
  const actorName = user?.displayName || user?.email?.split('@')[0] || 'Team';

  const targetKey = props.mode === 'contact' ? props.contactId : props.projectId;
  const displayName =
    props.mode === 'contact' ? props.contactName : props.clientName;
  const noteProjectId =
    props.mode === 'project' ? props.projectId : props.primaryProjectId;
  const emailContactId =
    props.mode === 'contact' ? props.contactId : props.clientId;
  const projectIdForConv = props.mode === 'project' ? props.projectId : null;
  const contactIdForConv = props.mode === 'contact' ? props.contactId : null;
  const syntheticKey = `lens:${props.mode}:${targetKey}`;

  /* The lens data source — the live source sliced SERVER-SIDE to this target. */
  const filter: ConversationFilter =
    props.mode === 'contact'
      ? { contactId: props.contactId }
      : { projectId: props.projectId };
  const dataSource = useMemo<InboxDataSource>(
    () => lensDataSource(filter),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.mode, targetKey],
  );

  /* Number registry — resolves the "received on" chip in the thread header. */
  const [registry, setRegistry] = useState<NumberRegistry>(fallbackRegistry);
  const registryRef = useRef<NumberRegistry>(registry);
  registryRef.current = registry;
  useEffect(() => subscribeNumberRegistry(setRegistry), []);

  /* ── List B — internal notes + email (never WhatsApp, never crossRef) ── */
  const [listB, setListB] = useState<Message[]>([]);
  useEffect(() => {
    setListB([]);
    if (props.mode === 'contact') {
      return subscribeContactNotesEmail(props.contactId, setListB, () => {});
    }
    return subscribeProjectNotesEmail(props.projectId, setListB, () => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.mode, targetKey]);
  const listBMsgs = useMemo(() => listB.map(listBToInboxMessage), [listB]);

  /* ── List A — WhatsApp conversation list (server-filtered) ── */
  const [rows, setRows] = useState<InboxConversation[] | null>(null);
  const [listErr, setListErr] = useState<string | null>(null);

  const [activeKey, setActiveKey] = useState<string | null>(null);
  const activeKeyRef = useRef<string | null>(null);
  activeKeyRef.current = activeKey;

  const [thread, setThread] = useState<ThreadData | null>(null);
  const [events, setEvents] = useState<ThreadEvent[]>([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [unreadAtOpen, setUnreadAtOpen] = useState(0);
  const [sending, setSending] = useState(false);
  const [sendErr, setSendErr] = useState<string | null>(null);
  const [attachError, setAttachError] = useState<string | null>(null);
  const [canned, setCanned] = useState<CannedReply[]>([]);

  const loadThread = useCallback(
    async (key: string, silent = false) => {
      if (!silent) {
        setThread(null);
        setEvents([]);
        setThreadLoading(true);
        setSendErr(null);
      }
      try {
        const [t, ev] = await Promise.all([
          dataSource.fetchThread(key),
          dataSource.fetchEvents(key).catch(() => [] as ThreadEvent[]),
        ]);
        if (activeKeyRef.current === key) {
          setThread(t);
          setEvents(ev);
        }
      } catch {
        if (!silent) setSendErr('Could not open that conversation.');
      } finally {
        if (!silent) setThreadLoading(false);
      }
    },
    [dataSource],
  );

  const openConversation = useCallback(
    (key: string) => {
      setActiveKey(key);
      setRows((prev) => {
        const row = prev?.find((r) => r.key === key);
        setUnreadAtOpen(row?.unread || 0);
        return prev
          ? prev.map((r) => (r.key === key ? { ...r, unread: 0 } : r))
          : prev;
      });
      void dataSource.markRead(key);
      void loadThread(key);
    },
    [dataSource, loadThread],
  );

  useEffect(() => {
    setRows(null);
    setActiveKey(null);
    setThread(null);
    return dataSource.subscribeList(
      (r) => {
        setRows((prev) => {
          if (!prev) return r;
          return r.map((row) =>
            row.key === activeKeyRef.current ? { ...row, unread: 0 } : row,
          );
        });
        setListErr(null);
      },
      setListErr,
      () => registryRef.current,
    );
  }, [dataSource]);

  /* Default-select the most-recent WA conversation once the list arrives. */
  useEffect(() => {
    if (!rows || rows.length === 0) return;
    if (activeKey && rows.some((r) => r.key === activeKey)) return;
    if (activeKey === null || !rows.some((r) => r.key === activeKey)) {
      openConversation(rows[0].key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  /* Live re-poll of the OPEN WA thread. */
  useEffect(() => {
    const id = window.setInterval(() => {
      const k = activeKeyRef.current;
      if (!k) return;
      if (typeof document !== 'undefined' && document.hidden) return;
      void loadThread(k, true);
    }, LIVE_POLL_MS);
    return () => window.clearInterval(id);
  }, [loadThread]);

  /* Canned replies for the ⚡ picker. */
  useEffect(() => {
    if (!activeKey) return;
    let cancelled = false;
    void (async () => {
      try {
        const meta = await fetchInboxMeta([activeKey]);
        if (cancelled) return;
        const list = visibleCanned(meta.canned || [], meta.me?.staffId ?? null);
        setCanned(
          list.length > 0
            ? list
            : CANNED_SEED.map((s) => seedToCanned(s, cannedVars)),
        );
      } catch {
        if (!cancelled) setCanned(CANNED_SEED.map((s) => seedToCanned(s, cannedVars)));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeKey]);

  /* ── WhatsApp send / react — the SAME single-message callables ── */
  const doSend = useCallback(
    async (text: string, attachment?: AttachmentInput): Promise<boolean> => {
      const key = activeKeyRef.current;
      if (!key) return false;
      setSending(true);
      setSendErr(null);
      try {
        if (attachment) await dataSource.sendAttachment(key, text || undefined, attachment);
        else await dataSource.sendText(key, text);
        await loadThread(key, true);
        return true;
      } catch (e) {
        setSendErr(sendErrorText(e));
        return false;
      } finally {
        setSending(false);
      }
    },
    [dataSource, loadThread],
  );

  const doSendTemplate = useCallback(
    async (templateName: string, params: string[]): Promise<boolean> => {
      const key = activeKeyRef.current;
      if (!key) return false;
      setSending(true);
      setSendErr(null);
      try {
        await dataSource.sendTemplate(key, templateName, params);
        await loadThread(key, true);
        return true;
      } catch (e) {
        setSendErr(templateErrorText(e));
        return false;
      } finally {
        setSending(false);
      }
    },
    [dataSource, loadThread],
  );

  const doSendCanned = useCallback(
    async (text: string): Promise<boolean> => {
      const key = activeKeyRef.current;
      if (!key || !text.trim()) return false;
      setSending(true);
      setSendErr(null);
      try {
        await dataSource.sendText(key, text);
        await loadThread(key, true);
        return true;
      } catch (e) {
        setSendErr(sendErrorText(e));
        return false;
      } finally {
        setSending(false);
      }
    },
    [dataSource, loadThread],
  );

  const doReact = useCallback(
    async (targetWaMessageId: string, emoji: string): Promise<boolean> => {
      const key = activeKeyRef.current;
      if (!key) return false;
      try {
        await dataSource.sendReaction(key, targetWaMessageId, emoji);
        return true;
      } catch {
        return false;
      }
    },
    [dataSource],
  );

  const onFetchOlderMessages = useCallback(
    (beforeMs: number): Promise<OlderMessagesResult> => {
      const key = activeKeyRef.current;
      if (!key) {
        return Promise.resolve({ messages: [], hasMore: false, nextCursor: null });
      }
      return dataSource.fetchOlderMessages(key, beforeMs);
    },
    [dataSource],
  );

  const onConfirmProposal = useCallback(
    (mid: string, pid: string, nt: string, pids: string[], title: string | null) =>
      dataSource.confirmProposal(mid, pid, nt, pids, title),
    [dataSource],
  );
  const onDismissProposal = useCallback(
    (mid: string, pid: string) => dataSource.dismissProposal(mid, pid),
    [dataSource],
  );
  const onCreateTask = useCallback(
    async (mid: string, pid: string, cid: string, title: string) => {
      await dataSource.createTask(mid, pid, cid, title);
    },
    [dataSource],
  );
  const noopAssign = useCallback(async () => false, []);

  /* ── Internal-note + email send paths (List B) — kept wired ── */
  const onSendNote = useMemo(() => {
    if (!noteProjectId) return undefined;
    return async (text: string): Promise<boolean> => {
      await postMessage(noteProjectId, {
        authorName: actorName,
        authorUid: user?.uid ?? 'unknown',
        body: text,
        contactId: emailContactId,
        internal: true,
      });
      return true;
    };
  }, [noteProjectId, actorName, user?.uid, emailContactId]);

  const onSendEmail = useMemo(() => {
    if (!noteProjectId || !emailContactId) return undefined;
    return async (text: string): Promise<boolean> => {
      await sendEmail({ projectId: noteProjectId, contactId: emailContactId, body: text });
      return true;
    };
  }, [noteProjectId, emailContactId]);

  /* ── Merge List A (WhatsApp) + List B (notes/email) → ONE Thread ── */
  const activeConversation = useMemo(
    () => rows?.find((r) => r.key === activeKey) ?? null,
    [rows, activeKey],
  );
  const hasWA = !!activeConversation;

  const mergedThread = useMemo<ThreadData | null>(() => {
    if (hasWA) {
      if (!thread) return null; // WA thread still loading
      const merged = [...thread.messages, ...listBMsgs].sort(
        (a, b) => (a.at ?? 0) - (b.at ?? 0),
      );
      return { ...thread, messages: merged };
    }
    // No live WhatsApp → a synthetic thread carrying only notes/email, so the
    // screen never blanks and note/email can still be sent/read.
    if (listBMsgs.length > 0 || noteProjectId) {
      return {
        key: syntheticKey,
        messages: [...listBMsgs].sort((a, b) => (a.at ?? 0) - (b.at ?? 0)),
        lastInboundMs: null,
        phone: null,
        hasMore: false,
        nextCursor: null,
        approvedTemplates: [],
      };
    }
    return null;
  }, [hasWA, thread, listBMsgs, noteProjectId, syntheticKey]);

  const syntheticConversation = useMemo<InboxConversation>(
    () => ({
      key: syntheticKey,
      name: displayName || 'this contact',
      phone: null,
      initials: '•',
      snippet: '',
      lastMessageAt: null,
      unread: 0,
      triage: false,
      aiHandled: false,
      aiBot: false,
      aiPaused: false,
      projectId: projectIdForConv,
      projectRef: null,
      contactId: contactIdForConv,
      waProfileName: null,
      number: null,
      receivingPhoneNumberId: null,
    }),
    [syntheticKey, displayName, projectIdForConv, contactIdForConv],
  );

  const conversationToRender = hasWA ? activeConversation : mergedThread ? syntheticConversation : null;

  const extraChannels: LensExtraChannels = {
    onSendNote,
    onSendEmail,
    noteUnavailableReason: onSendNote
      ? null
      : 'Internal notes attach to a project — link this contact to a project to add notes here.',
    emailUnavailableReason: onSendEmail
      ? null
      : 'Email becomes available once this contact has an email address and a project on file.',
    waUnavailableReason: hasWA
      ? null
      : 'No WhatsApp conversation yet — it appears here live the moment this contact messages, or once you reach out from the Team Inbox.',
    defaultChannel: hasWA ? 'wa' : onSendNote ? 'note' : onSendEmail ? 'email' : 'wa',
  };

  const multiple = (rows?.length ?? 0) > 1;
  const stillLoading = rows === null && listB.length === 0 && !mergedThread;

  return (
    <div className="lt-host">
      {listErr && (
        <div className="lt-err" role="alert">
          {listErr}
        </div>
      )}

      {/* WhatsApp conversation selector (only when a lens has >1 WA thread). */}
      {hasWA && multiple && rows && (
        <div className="lt-conv-picker" role="group" aria-label="WhatsApp conversations">
          {rows.map((r) => (
            <ConvChip
              key={r.key}
              label={r.projectRef || r.name || r.phone || 'Conversation'}
              sub={r.projectRef ? r.name || r.phone : r.phone}
              pressed={r.key === activeKey}
              unread={r.unread || 0}
              onClick={() => openConversation(r.key)}
            />
          ))}
        </div>
      )}

      {stillLoading ? (
        <div className="lt-thread-host lt-center">
          <p className="lt-empty">Loading conversation…</p>
        </div>
      ) : conversationToRender === null ? (
        <div className="lt-thread-host lt-center">
          <p className="lt-empty">
            No messages with {displayName || 'this contact'} yet. WhatsApp, email
            and internal notes all appear here — live — as soon as there is one.
          </p>
        </div>
      ) : (
        <div className="lt-thread-host ti-root">
          <Thread
            conversation={conversationToRender}
            thread={mergedThread}
            events={hasWA ? events : []}
            loading={hasWA ? threadLoading : false}
            sending={sending}
            sendErr={sendErr}
            unreadAtOpen={unreadAtOpen}
            replyFrom={null}
            teammates={[]}
            myId={null}
            isAdmin={isAdmin}
            canAssign={false}
            registry={registry}
            linkProject={true}
            onBack={() => setActiveKey(null)}
            onSend={doSend}
            onSendTemplate={doSendTemplate}
            onSendCanned={doSendCanned}
            canned={canned}
            onReact={doReact}
            onAssign={noopAssign}
            onClaim={null}
            onAttachError={setAttachError}
            onFetchOlderMessages={onFetchOlderMessages}
            onConfirmProposal={onConfirmProposal}
            onDismissProposal={onDismissProposal}
            onCreateTask={onCreateTask}
            extraChannels={extraChannels}
          />
        </div>
      )}

      {attachError && (
        <div className="lt-err" role="alert">
          {attachError}
        </div>
      )}
    </div>
  );
}
